import { Box, FormField, InputTextField, Typography } from '@tempmee/react';
import { OutlineLocationMarkerIcon } from '@tempmee/react-icons';
import { colors, shadows } from '@tempmee/tailwind';
import { Controller, useFormContext } from 'react-hook-form';
import PlacesAutocomplete from 'react-places-autocomplete';
import type { ISignUpFormValues } from '../types';

const SEARCH_OPTIONS = {
  componentRestrictions: { country: ['us'] },
  types: ['address'],
};

export interface IOfficeAddressFieldProps {
  /** Fires with the `placeId` when the user picks a suggestion; `null` when
   *  the text changes without selecting. The parent owns this state because
   *  it's what drives the BE address-add call. */
  onPlaceIdChange: (placeId: string | null) => void;
}

export const OfficeAddressField = ({ onPlaceIdChange }: IOfficeAddressFieldProps) => {
  const { control, clearErrors } = useFormContext<ISignUpFormValues>();

  return (
    <FormField
      label="Office Address"
      htmlFor="officeAddress"
      TrailingComponent={
        <Typography variant="body1" color={colors.neutral[500]}>
          (Click the address when it appears below)
        </Typography>
      }
    >
      <Controller
        name="officeAddress"
        control={control}
        rules={{ required: 'Office address is required' }}
        render={({ field, fieldState }) => (
          <PlacesAutocomplete
            value={field.value}
            onChange={(value) => {
              field.onChange(value);
              onPlaceIdChange(null);
              clearErrors('officeAddress');
            }}
            onSelect={(address, selectedPlaceId) => {
              field.onChange(address);
              onPlaceIdChange(selectedPlaceId);
              clearErrors('officeAddress');
            }}
            debounce={300}
            searchOptions={SEARCH_OPTIONS}
          >
            {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
              <Box style={{ position: 'relative' }} data-cy="office-address-suggestions">
                <InputTextField
                  data-cy="input-office-address"
                  id="officeAddress"
                  placeholder="Enter your full address"
                  isError={Boolean(fieldState.error)}
                  leftIcon={<OutlineLocationMarkerIcon size={24} color={colors.secondary[500]} />}
                  {...getInputProps({ onBlur: field.onBlur, ref: field.ref })}
                />
                {(loading || suggestions.length > 0) && (
                  <Box
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      marginTop: 4,
                      backgroundColor: colors.shades[0],
                      borderRadius: 10,
                      boxShadow: shadows['elevation-4'],
                      zIndex: 10,
                      overflow: 'hidden',
                    }}
                  >
                    {loading && (
                      <Box style={{ padding: 12 }}>
                        <Typography variant="body1" color={colors.neutral[500]}>
                          Loading...
                        </Typography>
                      </Box>
                    )}
                    {suggestions.map((suggestion) => {
                      // Strip `key` off the spread so React doesn't warn —
                      // we set it explicitly on <Box>.
                      // eslint-disable-next-line @typescript-eslint/no-unused-vars
                      const { key: _k, ...itemProps } = getSuggestionItemProps(suggestion);
                      return (
                        <Box
                          key={suggestion.placeId}
                          {...itemProps}
                          data-cy="office-address-suggestion"
                          style={{
                            padding: 12,
                            cursor: 'pointer',
                            backgroundColor: suggestion.active
                              ? colors.neutral[50]
                              : colors.shades[0],
                          }}
                        >
                          <Typography variant="body1" color={colors.neutral[600]}>
                            {suggestion.description}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}
          </PlacesAutocomplete>
        )}
      />
    </FormField>
  );
};
